import React, { useEffect, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Card } from '../components/Card';
import { IconPill } from '../components/IconPill';
import { useGabarit } from '../hooks/useGabarit';
import { ScreenHeader } from '../components/ScreenHeader';
import { SubscriptionExpiryNotice } from '../components/SubscriptionExpiryNotice';
import { internalNotifications } from '../data/notifications';
import { getSubscriptionStatus } from '../services/profile';
import { expiryReminderDays, type SubscriptionStatus } from '../utils/subscriptionExpiry';
import { colors, layout, screenPadding, spacing, typography } from '../theme/tokens';
import { t } from '../i18n';

export function NotificationsSettingsScreen({ onBack }: { onBack?: () => void }) {
  const [sub, setSub] = useState<SubscriptionStatus | null>(null);

  // Échec silencieux : le rappel reste simplement masqué (dormant), le reste de l'écran fonctionne.
  useEffect(() => {
    getSubscriptionStatus()
      .then(setSub)
      .catch(() => {});
  }, []);

  const expiryDays = expiryReminderDays(sub);

  const { large } = useGabarit();
  const sIntro = (
    <>
        <Text style={styles.intro}>{t('notifications_screen.settingsSubtitle')}</Text>

        {/* Rappel d'expiration : affiché uniquement si un abonnement payant a une échéance connue.
            Les comptes de la bêta « pro pour tous » en ont une (migrations 0024 puis 0036) : le
            rappel leur décompte donc bien la fin de bêta. Masqué sans échéance (free, école). */}
        {sub && expiryDays !== null ? (
          <SubscriptionExpiryNotice days={expiryDays} source={sub.source} testID="notif-settings-expiry" />
        ) : null}

            </>
  );
  const sConseils = (
    <>
<Text style={styles.sectionLabel}>{t('notifications_screen.tips')}</Text>
        {internalNotifications().map((n) => (
          <Card key={n.id} testID={`notif-settings-tip-${n.id}`}>
            <View style={styles.row}>
              <IconPill icon={n.icon} accent={n.accent} variant="soft" size={40} />
              <View style={styles.texts}>
                <Text style={styles.title}>{n.title}</Text>
                <Text style={styles.body}>{n.body}</Text>
              </View>
            </View>
          </Card>
        ))}
    </>
  );

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <ScreenHeader onBack={onBack} title={t('notifications_screen.title')} testID="notif-settings-back" />
              {large ? (
          <ScrollView contentContainerStyle={styles.colonnesL} showsVerticalScrollIndicator={false}>
            <View style={styles.colGaucheL}>
              {sIntro}
            </View>
            <View style={styles.colDroiteL}>
              {sConseils}
            </View>
          </ScrollView>
        ) : (
          <ScrollView contentContainerStyle={styles.content}>
            {sIntro}
            {sConseils}
          </ScrollView>
        )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  colonnesL: {
    flexGrow: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.xl,
    width: '100%',
    maxWidth: layout.pageMaxWidth,
    alignSelf: 'center',
    padding: spacing.xxl + spacing.xl,
  },
  colGaucheL: { flex: 1, minWidth: 0, gap: spacing.lg },
  colDroiteL: { flex: 1, minWidth: 0, gap: spacing.md },
  piedL: { marginTop: spacing.sm },
  container: { flex: 1, backgroundColor: colors.grey50 },
  content: { padding: screenPadding, gap: spacing.md },
  intro: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey500 },
  sectionLabel: {
    marginTop: spacing.xs,
    fontSize: typography.caption.size,
    fontWeight: typography.caption.weight,
    letterSpacing: typography.caption.letterSpacing,
    color: colors.grey500,
  },
  row: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.md },
  texts: { flex: 1, gap: spacing.xs },
  title: { fontSize: typography.h3.size, fontWeight: typography.h3.weight, color: colors.grey900 },
  body: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey700 },
});
